import React, { useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import Drilldown from '../../components/Drilldown';
import { TEAM_LABELS } from '../../config';
import { fmtMoney, fmtNumber, useFetch } from '../../hooks';
import { Alert, Button, Card, Progress, SearchBox, Skeleton, Stat } from '../../ui/basics';
import { Donut, accentColor } from '../../ui/charts';

const TEAM_KEYS = {
  LI: ['LightIndustry_details', 'LI_active_candidates', 'LI_active_margin'],
  HC: ['Healthcare_details', 'HC_active_candidates', 'HC_active_margin'],
  IT: ['IT_details', 'It_active_candidates', 'It_active_margin'],
};

export default function Overview({ org, lead }) {
  const { teams, allTeams } = useAuth();
  const { data, loading, error } = useFetch('reports/overview/', { organization: org, lead });
  const [drill, setDrill] = useState(null);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const stats = data && data.data;

  const clients = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.candidates_details[0].active_candidates)
      .map(([name, c]) => ({ name, active: c.active || 0, inactive: c.Inactive || 0, margin: c.margin || 0 }))
      .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => b.margin - a.margin);
  }, [stats, search]);

  if (error) return <Alert>{error}</Alert>;
  if (loading || !stats) return <div className="grid cols-3">{[0, 1, 2].map((i) => <div key={i} className="card" style={{ padding: 20 }}><Skeleton w="40%" /><div style={{ height: 12 }} /><Skeleton h={30} w="60%" /></div>)}</div>;

  const activeMargin = Number(stats.Active_margin);
  const open = (title, filters) => setDrill({ title, filters });
  const visibleTeams = Object.keys(TEAM_KEYS).filter((t) => !lead || lead === t).filter((t) => allTeams || teams.includes(t));
  const top = stats.Top_Recuiter_details[0];
  const shown = showAll ? clients : clients.slice(0, 12);
  const share = (part, whole) => (whole ? `${((part / whole) * 100).toFixed(1)}%` : '0%');

  return (
    <div className="stack">
      <div className="grid cols-3">
        <Stat label="Active margin" value={fmtMoney(activeMargin)} sub="Weekly gross margin of active candidates" onClick={() => open('Active placements', { status: 'Active' })} />
        <Stat label="Active candidates" value={fmtNumber(stats.Active_candidates)}
          sub={`${share(stats.Active_candidates, stats.Active_candidates + stats.Inactive_candidates)} of all candidates`} onClick={() => open('Active candidates', { status: 'Active' })} />
        <Stat label="Inactive candidates" value={fmtNumber(stats.Inactive_candidates)}
          sub={`${share(stats.Inactive_candidates, stats.Active_candidates + stats.Inactive_candidates)} of all candidates`} onClick={() => open('Inactive candidates', { status: 'Inactive' })} />
      </div>

      <div className="grid cols-3">
        <Card title="Candidates" subtitle="Active vs inactive">
          <Donut data={[{ id: 'Active', value: stats.Active_candidates }, { id: 'Inactive', value: stats.Inactive_candidates }]}
            colors={[accentColor(), '#e6e9f0']} onClick={(d) => open(`${d.id} candidates`, { status: d.id })} />
        </Card>
        <Card title="Teams" subtitle="Active margin by team (click to see candidates)">
          <div className="stack" style={{ gap: 18 }}>
            {visibleTeams.map((t) => {
              const [group, countKey, marginKey] = TEAM_KEYS[t];
              const row = stats[group][0];
              return (
                <div key={t} role="button" tabIndex={0} style={{ cursor: 'pointer' }} onClick={() => open(`${TEAM_LABELS[t]} · active`, { status: 'Active', lead: t })}
                  onKeyDown={(e) => e.key === 'Enter' && open(`${TEAM_LABELS[t]} · active`, { status: 'Active', lead: t })}>
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                    <b>{TEAM_LABELS[t]}</b><span className="num">{fmtMoney(row[marginKey])} <span className="muted">· {row[countKey]} candidates</span></span>
                  </div>
                  <Progress value={Number(row[marginKey])} max={activeMargin} />
                </div>
              );
            })}
          </div>
        </Card>
        <Card title="Top recruiter" subtitle="By active margin">
          {top.Top_Recuiter ? (
            <div className="stack">
              <div className="row"><span className="avatar">{top.Top_Recuiter.split(' ').map((p) => p[0]).slice(0, 2).join('')}</span><b>{top.Top_Recuiter}</b></div>
              <div className="grid cols-2">
                <div><div className="muted">Margin</div><b className="num">{fmtMoney(top.top_margin)}</b><div className="muted">{share(Number(top.top_margin), activeMargin)} of total</div></div>
                <div><div className="muted">Active candidates</div><b className="num">{top.active_candidates}</b><div className="muted">{share(top.active_candidates, stats.Active_candidates)} of total</div></div>
              </div>
              <Button size="sm" onClick={() => open(`${top.Top_Recuiter} · active`, { status: 'Active', recruiter: top.Top_Recuiter })}>View candidates</Button>
            </div>
          ) : <p className="muted">No active candidates.</p>}
        </Card>
      </div>

      {stats.organization_breakdown && (
        <Card flush title="Organizations" subtitle="Combined totals by organization">
          <div className="table-wrap"><table className="table"><thead><tr><th>Organization</th><th className="r">Active candidates</th><th className="r">Inactive candidates</th><th className="r">Active margin</th></tr></thead>
            <tbody>{stats.organization_breakdown.map((o) => (
              <tr key={o.organization}><td><b>{o.organization}</b></td><td className="r num">{o.Active_candidates}</td><td className="r num">{o.Inactive_candidates}</td><td className="r num">{fmtMoney(o.Active_margin)}</td></tr>
            ))}</tbody></table></div>
        </Card>
      )}

      <Card flush title="Clients" subtitle={`${clients.length} clients · sorted by active margin`} actions={<SearchBox value={search} onChange={setSearch} placeholder="Find a client…" />}>
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Client</th><th className="r">Active</th><th className="r">Inactive</th><th className="r">Active margin</th></tr></thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.name} className="clickable" onClick={() => open(c.name, { client: c.name })}>
                <td><b>{c.name}</b></td>
                <td className="r num"><button type="button" className="link" onClick={(e) => { e.stopPropagation(); open(`${c.name} · active`, { client: c.name, status: 'Active' }); }}>{c.active}</button></td>
                <td className="r num"><button type="button" className="link" onClick={(e) => { e.stopPropagation(); open(`${c.name} · inactive`, { client: c.name, status: 'Inactive' }); }}>{c.inactive}</button></td>
                <td className="r num">{c.margin ? fmtMoney(c.margin) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        {clients.length > 12 && <div className="pager"><Button size="sm" onClick={() => setShowAll(!showAll)}>{showAll ? 'Show top 12' : `Show all ${clients.length}`}</Button></div>}
      </Card>

      {drill && <Drilldown title={drill.title} org={org} lead={lead} filters={drill.filters} onClose={() => setDrill(null)} />}
    </div>
  );
}
